import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, X, BookOpen, UsersRound, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import { classEntryApi, broadcastApi } from '../../api/examApi';
import { DashboardLayout } from '../../components/layout';
import { parseApiError } from '../../lib/apiErrors';

const todayIso = () => new Date().toISOString().slice(0, 10);

function CreateEntryModal({ groups, onClose, onSubmit }) {
  const [groupId, setGroupId] = useState('');
  const [date, setDate] = useState(todayIso());
  const [title, setTitle] = useState('');
  const [notesMessage, setNotesMessage] = useState('');
  const [facultyComment, setFacultyComment] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await onSubmit({
        group_id: groupId,
        date,
        title: title.trim(),
        notes_message: notesMessage.trim() || null,
        faculty_comment: facultyComment.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(parseApiError(err, 'Failed to create class entry').message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-heading font-bold text-slate-900 dark:text-white">New Class Entry</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Group</label>
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                required
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                data-testid="entry-group-select"
              >
                <option value="">Select group…</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                data-testid="entry-date-input"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="e.g. Quadratic equations"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              data-testid="entry-title-input"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Notes / Message (optional)</label>
            <textarea
              value={notesMessage}
              onChange={(e) => setNotesMessage(e.target.value)}
              rows={2}
              placeholder="What was covered, instructions for students…"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Faculty Comment (optional)</label>
            <textarea
              value={facultyComment}
              onChange={(e) => setFacultyComment(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
          </div>

          {error && <p className="text-sm text-rose-500" data-testid="entry-form-error">{error}</p>}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !groupId || !date || !title.trim()}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
              data-testid="entry-form-submit"
            >
              {saving ? 'Saving…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ClassEntriesPage() {
  const [entries, setEntries] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterGroup, setFilterGroup] = useState('');

  useEffect(() => {
    broadcastApi.getEligibleGroups().then(({ data }) => setGroups(data)).catch(() => toast.error('Failed to load groups'));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterGroup) params.group_id = filterGroup;
    classEntryApi.list(params)
      .then(({ data }) => setEntries(data))
      .catch(() => toast.error('Failed to load class entries'))
      .finally(() => setLoading(false));
  }, [filterGroup]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const byDate = new Map();
    for (const e of entries) {
      if (!byDate.has(e.date)) byDate.set(e.date, []);
      byDate.get(e.date).push(e);
    }
    return Array.from(byDate.entries()).sort(([a], [b]) => b.localeCompare(a));
  }, [entries]);

  const handleCreate = async (payload) => {
    await classEntryApi.create(payload);
    toast.success('Class entry created');
    load();
  };

  return (
    <DashboardLayout title="Classroom">
      <div className="animate-fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">Classroom</h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1">Class notes, homework, and per-student tracking</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            disabled={groups.length === 0}
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary-light text-white shadow-sm rounded-md px-4 py-2.5 transition-colors duration-200 font-medium disabled:opacity-50"
            data-testid="create-entry-btn"
          >
            <Plus size={20} />
            New Entry
          </button>
        </div>

        {groups.length === 0 && !loading && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-300">
            You're not linked to any group yet. Ask an admin to assign you to a group before creating class entries.
          </div>
        )}

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 flex flex-wrap items-center gap-4">
          <select
            value={filterGroup}
            onChange={(e) => setFilterGroup(e.target.value)}
            className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white"
            data-testid="filter-group-select"
          >
            <option value="">All groups</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 rounded-lg bg-slate-100 dark:bg-slate-800" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">No class entries yet.</div>
        ) : (
          <div className="space-y-6">
            {grouped.map(([date, items]) => (
              <div key={date}>
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2">
                  {new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </h3>
                <div className="space-y-2">
                  {items.map((entry) => (
                    <Link
                      key={entry.id}
                      to={`/faculty/classroom/${entry.id}`}
                      className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 hover:border-primary dark:hover:border-accent-cool transition-colors"
                      data-testid={`class-entry-${entry.id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                          <span className="inline-flex items-center gap-1.5 font-medium text-slate-900 dark:text-white">
                            <BookOpen size={14} className="text-primary dark:text-accent-cool" />
                            {entry.title}
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                            <UsersRound size={14} /> {entry.group_name}
                          </span>
                          {entry.files?.length > 0 && (
                            <span className="text-xs text-slate-400">{entry.files.length} file{entry.files.length === 1 ? '' : 's'}</span>
                          )}
                        </div>
                        {entry.notes_message && (
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 truncate">{entry.notes_message}</p>
                        )}
                      </div>
                      <ChevronRight size={18} className="text-slate-400 shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateEntryModal groups={groups} onClose={() => setShowCreate(false)} onSubmit={handleCreate} />
      )}
    </DashboardLayout>
  );
}
